// ============================================================
// FICHIER : boutique/modules/api.js
// RÔLE : Couche d'appels API du widget : header de session ajouté
//        automatiquement, notifications automatiques selon la
//        réponse, et chargement des images protégées en blob.
// ============================================================

window.Toilesia = window.Toilesia || {};
(function () {
    'use strict';

    const config = Toilesia.config;

    /**
     * Appelle une route de l'API en garantissant la session et en
     * ajoutant le header X-Session-Token.
     * @param {string} route   ex. 'image/generate'
     * @param {string} method  'GET' | 'POST'
     * @param {*} body         null | objet JSON | FormData
     * @param {object} [opts]  { silencieux: true } pour ne pas notifier
     *                         automatiquement (ex. rechargement historique)
     * @returns {Promise<object>} Réponse JSON du serveur
     */
    async function appeler(route, method = 'POST', body = null, opts = {}) {
        await Toilesia.session.garantir();
        const reponse = await ToilesiaAuth.apiCall(
            route,
            method,
            body,
            Toilesia.session.headers()
        );
        if (!opts.silencieux) {
            notifierReponse(route, reponse);
        }
        return reponse;
    }

    /**
     * Message utilisateur d'une réponse API en ÉCHEC (mêmes libellés que
     * les notifications automatiques). Exposé pour les appels lancés en
     * `{ silencieux: true }` qui gèrent eux-mêmes l'affichage — ex. l'upload,
     * qui vérifie d'abord que l'image n'a pas été enregistrée malgré tout.
     * @param {object} reponse Réponse renvoyée par apiCall()
     * @returns {string}
     */
    function messageErreur(reponse) {
        const r = reponse || {};
        const code = r.statut_http || 0;
        if (code === 429) {
            return 'Trop de requêtes. Patientez un instant puis réessayez.';
        }
        if (code === 0) {
            return 'Erreur réseau : vérifiez votre connexion.';
        }
        return r.erreur || 'Une erreur est survenue.';
    }

    /**
     * Notifie automatiquement l'utilisateur selon la réponse serveur
     * (succès / erreur / code HTTP : 409 anti-doublon, 429 rate limit...).
     */
    function notifierReponse(route, reponse) {
        const n = Toilesia.notifier;
        if (!n) return;

        if (reponse.succes) {
            if (route === 'image/upload') {
                n.succes(reponse.message || 'Photo envoyée avec succès.');
            } else if (route === 'image/generate') {
                n.succes('Votre œuvre est prête !');
            } else if (route === 'image/generate_number_paint') {
                n.succes('Aperçu peinture par numéro généré.');
            } else if (route === 'session/connect') {
                n.succes(reponse.message || 'Projet sauvegardé !');
            }
            return;
        }

        // Erreurs / avertissements selon le code HTTP
        const code = reponse.statut_http || 0;
        if (code === 409) {
            // Anti-doublon : l'œuvre existe déjà (information, pas une panne)
            n.avertissement(reponse.erreur || 'Cette œuvre existe déjà.');
            return;
        }
        if (code === 429) {
            // Rate limit : ce n'est pas une panne, on invite à patienter
            n.avertissement('Trop de requêtes. Patientez un instant puis réessayez.');
            return;
        }
        n.erreur(messageErreur(reponse));
    }

    /**
     * En-têtes d'un appel protégé : en-têtes de sécurité client
     * (X-Auth-*, fabriqués par auth-client.js) + header de session.
     * ⚠️ Nécessaires même pour un appel BINAIRE : index.php (bloc 4) exige
     *    X-Auth-Token / X-Auth-Date / X-Auth-Salt sur toutes les routes
     *    sauf /ping et /test, sinon « 403 Cle API invalide ou manquante. ».
     *    apiCall() ne convient pas ici (il lit une réponse JSON).
     * @returns {Promise<object>}
     */
    async function enTetesSecurite() {
        const session = Toilesia.session.headers();
        if (window.ToilesiaAuth && typeof ToilesiaAuth.headersSecurite === 'function') {
            return ToilesiaAuth.headersSecurite(session);
        }
        // Repli : ancienne version de auth-client.js (CDN) → appel 403.
        console.error(
            "[Toilesia.api] auth-client.js obsolète : ToilesiaAuth.headersSecurite() " +
            "absent — l'API refusera la requête (403 Cle API invalide ou manquante.)."
        );
        return session;
    }

    /**
     * Charge une image protégée et retourne une URL objet affichable.
     * En-têtes envoyés : X-Auth-* + X-Session-Token (voir enTetesSecurite).
     * ⚠️ L'appelant doit révoquer l'URL avec URL.revokeObjectURL quand
     *    l'image n'est plus affichée.
     * @param {string} url Chemin relatif ex. 'storage/uploads/apercu_x.jpg'
     * @returns {Promise<string>} URL blob (blob:...)
     */
    async function chargerImage(url) {
        await Toilesia.session.garantir();
        const headers = await enTetesSecurite();
        const response = await fetch(
            config.apiUrl + '/image/download?url=' + encodeURIComponent(url),
            { headers: headers }
        );
        if (!response.ok) {
            throw new Error("Impossible de charger l'image.");
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    }

    Toilesia.api = {
        appeler: appeler,
        chargerImage: chargerImage,
        messageErreur: messageErreur
    };
})();
